import { useState } from 'react';
import moment from 'moment';
import {
  Button,
  Dropdown,
  MultiSelect,
  DatePicker,
  DatePickerInput,
  InlineNotification,
  InlineLoading,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
} from '@carbon/react';
import { Renew, ArrowRight } from '@carbon/react/icons';
import { Link } from 'react-router-dom';
import routes from '~/routes';
import { SidePanel } from '@carbon/ibm-products';
import { SidePanelResizeHandle } from 'components/resizable-side-panel';
import {
  ACTIVITY_SOURCE_LABEL,
  ENTITY_TYPE_ITEMS,
  type EntityType,
} from 'lib/entities';
import { format0 } from 'utils/number';
import { fromDateParam, SERVER_DATE_FORMAT, toDateParam } from 'utils/dates';
import type {
  EntityActivityDirection,
  EntityActivitySource,
  EntityLibraryAnalyticsLeader,
} from 'types/api-responses';
import { AnalyticsTrendChart } from './analytics-trend-chart';
import { useClearPagePushOnUnmount, useEntityLibraryAnalytics } from './hooks';
// Enables the @carbon/ibm-products SidePanel feature flag (shared config).
import 'components/inline-edit/ibm-products-config';
import styles from './library-analytics.module.scss';

const PANEL_CLASS = 'entity-analytics-sidepanel';
const PAGE_CONTENT = '#main-content';
// Must equal the SidePanel `size` token width (lg = 40rem) so the grip stays
// flush with the panel edge before the resize handle's width var applies.
const DEFAULT_WIDTH = 640;
const MAX_WINDOW_MONTHS = 3;
// Rendered-row cap; the totals row still reflects every user.
const MAX_LEADERBOARD_ROWS = 50;

interface LibraryAnalyticsPanelProps {
  open: boolean;
  onClose: () => void;
}

const SOURCE_ITEMS: { id: EntityActivitySource; label: string }[] = (
  Object.entries(ACTIVITY_SOURCE_LABEL) as [EntityActivitySource, string][]
).map(([id, label]) => ({ id, label }));

// Leaderboard columns — every source/direction always shows, even at zero.
const SOURCE_DIRECTIONS: {
  source: EntityActivitySource;
  direction: EntityActivityDirection;
}[] = [
  { source: 'document_ingest', direction: 'add' },
  { source: 'manual_add', direction: 'add' },
  { source: 'add_as_new', direction: 'add' },
  { source: 'add_as_new', direction: 'remove' },
  { source: 'deduplicate', direction: 'remove' },
  { source: 'bulk_deduplicate', direction: 'remove' },
  { source: 'reconcile_entity_page', direction: 'remove' },
  { source: 'reconcile_entity_page', direction: 'alias' },
  { source: 'reconcile_document', direction: 'remove' },
  { source: 'reconcile_document', direction: 'alias' },
  { source: 'mark_as_correct', direction: 'remove' },
  { source: 'mark_as_correct', direction: 'alias' },
  { source: 'direct_alias', direction: 'alias' },
  { source: 'operator_feed', direction: 'add' },
];

// Contiguous same-source entries collapse into one spanning top-header cell
// (e.g. "Add as New" spans its Added + Removed columns).
const SOURCE_GROUPS = SOURCE_DIRECTIONS.reduce<
  { source: EntityActivitySource; directions: EntityActivityDirection[] }[]
>((groups, { source, direction }) => {
  const last = groups.at(-1);
  if (last?.source === source) last.directions.push(direction);
  else groups.push({ source, directions: [direction] });
  return groups;
}, []);

// Lookback presets: ranges are computed on selection so "today" stays current;
// `Custom` (no range) is the shown state when the dates match no preset.
interface QuickRange {
  id: string;
  label: string;
  range: (() => [moment.Moment, moment.Moment]) | null;
}
const QUICK_RANGES: QuickRange[] = [
  { id: 'custom', label: 'Custom', range: null },
  { id: 'today', label: 'Today', range: () => [moment(), moment()] },
  {
    id: 'yesterday',
    label: 'Yesterday',
    range: () => [moment().subtract(1, 'day'), moment().subtract(1, 'day')],
  },
  {
    id: 'last3',
    label: 'Last 3 days',
    range: () => [moment().subtract(3, 'days'), moment()],
  },
  {
    id: 'last7',
    label: 'Last 7 days',
    range: () => [moment().subtract(7, 'days'), moment()],
  },
  {
    id: 'last30',
    label: 'Last 30 days',
    range: () => [moment().subtract(30, 'days'), moment()],
  },
];

const DIRECTION_LABEL: Record<EntityActivityDirection, string> = {
  add: 'Added',
  remove: 'Removed',
  alias: 'Aliased',
};

/**
 * The entity library's analytics side panel: trend chart and per-user
 * contribution leaderboard over a date window, with a link to the detailed log.
 * @param {LibraryAnalyticsPanelProps} props - Whether it is open, and its close handler
 * @return {JSX.Element} The panel
 */
export function LibraryAnalyticsPanel({
  open,
  onClose,
}: LibraryAnalyticsPanelProps) {
  const [startDate, setStartDate] = useState<string>(
    moment().subtract(MAX_WINDOW_MONTHS, 'months').format(SERVER_DATE_FORMAT)
  );
  const [endDate, setEndDate] = useState<string>(
    moment().format(SERVER_DATE_FORMAT)
  );
  const [entityTypes, setEntityTypes] = useState<EntityType[]>([]);
  const [sources, setSources] = useState<EntityActivitySource[]>([]);

  useClearPagePushOnUnmount(PAGE_CONTENT);

  // Max window enforced as a validation error (query skipped) — clamping the
  // picker fought flatpickr's close/re-fire behavior.
  const rangeTooLong = moment(endDate).isAfter(
    moment(startDate).add(MAX_WINDOW_MONTHS, 'months')
  );

  // Preset matching the current window (else Custom), so manual date edits
  // reflect in the dropdown too.
  const activeQuickRange =
    QUICK_RANGES.find((option) => {
      if (!option.range) return false;
      const [start, end] = option.range();
      return (
        start.format(SERVER_DATE_FORMAT) === startDate &&
        end.format(SERVER_DATE_FORMAT) === endDate
      );
    }) ?? QUICK_RANGES[0];

  const { data, loading, error, refresh } = useEntityLibraryAnalytics({
    startDate,
    endDate,
    entityTypes,
    sources,
    enabled: open && !rangeTooLong,
  });

  return (
    <>
      <SidePanelResizeHandle
        open={open}
        panelSelector={`.${PANEL_CLASS}`}
        pageContentSelector={PAGE_CONTENT}
        defaultWidthPx={DEFAULT_WIDTH}
        showGrip
      />
      <SidePanel
        open={open}
        onRequestClose={onClose}
        title="Entity Library Analytics"
        subtitle="Real-time — state and activity over the selected window"
        className={PANEL_CLASS}
        placement="right"
        size="lg"
        slideIn
        selectorPageContent={PAGE_CONTENT}
      >
        <div className={styles.content}>
          {/* Reserved-height slot so the refetch indicator never pushes the
              controls (or an open menu) down mid-interaction. */}
          <div className={styles.statusRow} aria-live="polite">
            {loading && <InlineLoading description="Loading…" />}
            <Button
              className={styles.refreshButton}
              kind="ghost"
              size="sm"
              renderIcon={Renew}
              onClick={refresh}
              disabled={rangeTooLong}
            >
              Refresh
            </Button>
            {/* Drill-down: the per-action detail behind these aggregates. */}
            <Button<typeof Link>
              as={Link}
              to={routes.adminEntityCleanupLogPath}
              kind="ghost"
              size="sm"
              renderIcon={ArrowRight}
            >
              View detailed log
            </Button>
          </div>

          <div className={styles.dateField}>
            <div className={styles.controls}>
              <DatePicker
                datePickerType="range"
                dateFormat="m/d/Y"
                maxDate={new Date()}
                value={[fromDateParam(startDate), fromDateParam(endDate)]}
                onChange={(dates: Date[]) => {
                  if (dates.length !== 2) return;
                  setStartDate(toDateParam(dates[0]));
                  setEndDate(toDateParam(dates[1]));
                }}
              >
                <DatePickerInput
                  id="analytics-start"
                  labelText="From"
                  placeholder="mm/dd/yyyy"
                  size="sm"
                />
                <DatePickerInput
                  id="analytics-end"
                  labelText="To"
                  placeholder="mm/dd/yyyy"
                  size="sm"
                />
              </DatePicker>
              <Dropdown
                id="analytics-quick-range"
                className={styles.quickRange}
                size="sm"
                titleText="Lookback period"
                label="Lookback period"
                items={QUICK_RANGES}
                itemToString={(item: QuickRange | null) => item?.label ?? ''}
                selectedItem={activeQuickRange}
                onChange={({
                  selectedItem,
                }: {
                  selectedItem: QuickRange | null;
                }) => {
                  if (!selectedItem?.range) return;
                  const [start, end] = selectedItem.range();
                  setStartDate(start.format(SERVER_DATE_FORMAT));
                  setEndDate(end.format(SERVER_DATE_FORMAT));
                }}
              />
            </div>
            {rangeTooLong ? (
              <InlineNotification
                kind="error"
                lowContrast
                hideCloseButton
                title={`Maximum date range is ${MAX_WINDOW_MONTHS} months`}
                subtitle="Adjust the dates to continue."
              />
            ) : (
              <p className={styles.sectionNote}>
                Window is limited to {MAX_WINDOW_MONTHS} months.
              </p>
            )}
          </div>

          {/* Filter state outlives the panel (children unmount on close), so seed
              the remounting MultiSelects or a reopened panel shows empty
              selects over filtered data. */}
          <div className={styles.controls}>
            <MultiSelect
              id="analytics-entity-types"
              className={styles.filterSelect}
              size="sm"
              label="Entity type"
              titleText="Entity type"
              items={ENTITY_TYPE_ITEMS}
              initialSelectedItems={entityTypes}
              itemToString={(item) => item ?? ''}
              onChange={({ selectedItems }) =>
                setEntityTypes(selectedItems ?? [])
              }
            />
            <MultiSelect
              id="analytics-sources"
              className={styles.sourceSelect}
              size="sm"
              label="Activity source"
              titleText="Activity source"
              items={SOURCE_ITEMS}
              initialSelectedItems={SOURCE_ITEMS.filter((item) =>
                sources.includes(item.id)
              )}
              itemToString={(item) => item?.label ?? ''}
              onChange={({ selectedItems }) =>
                setSources((selectedItems ?? []).map((item) => item.id))
              }
            />
          </div>

          {error && (
            <InlineNotification
              kind="error"
              lowContrast
              hideCloseButton
              title="Couldn't load analytics"
              subtitle="Try Refresh; if it persists, contact support."
            />
          )}

          {data && (
            <AnalyticsTrendChart trend={data.trend} window={data.window} />
          )}

          {data && <Leaderboard leaders={data.leaderboard} />}
        </div>
      </SidePanel>
    </>
  );
}

// Carbon's TableHeader types omit `rowSpan` but spread it to the <th>; pass it
// via spread so the User / Total headers can span both rows without a type error.
const spanBothHeaderRows = { rowSpan: 2 };

/**
 * Per-user contributions by source and direction, with an all-users totals row.
 * @param {object} props - The leaderboard
 * @param {EntityLibraryAnalyticsLeader[]} props.leaders - One entry per contributor
 * @return {JSX.Element} The leaderboard table, or an empty-state note
 */
function Leaderboard({ leaders }: { leaders: EntityLibraryAnalyticsLeader[] }) {
  if (!leaders.length) {
    return (
      <div className={styles.section}>
        <h5 className={styles.sectionTitle}>Contribution by user</h5>
        <p className={styles.sectionNote}>No contributions in this window.</p>
      </div>
    );
  }

  /**
   * One leader's count for a source and direction.
   * @param {EntityLibraryAnalyticsLeader} leader - The contributor
   * @param {EntityActivitySource} source - The source
   * @param {EntityActivityDirection} direction - The direction
   * @return {number} The count, 0 when absent
   */
  const countFor = (
    leader: EntityLibraryAnalyticsLeader,
    source: EntityActivitySource,
    direction: EntityActivityDirection
  ) =>
    leader.bySource.find(
      (s) => s.source === source && s.direction === direction
    )?.count ?? 0;
  /**
   * A source/direction column's total across every user (accurate even though
   * rows are capped for display).
   * @param {EntityActivitySource} source - The source
   * @param {EntityActivityDirection} direction - The direction
   * @return {number} The total
   */
  const columnTotal = (
    source: EntityActivitySource,
    direction: EntityActivityDirection
  ) => leaders.reduce((sum, l) => sum + countFor(l, source, direction), 0);
  const grandTotal = leaders.reduce((sum, l) => sum + l.total, 0);

  const shown = leaders.slice(0, MAX_LEADERBOARD_ROWS);

  return (
    <div className={styles.section}>
      <h5 className={styles.sectionTitle}>Contribution by user</h5>
      {leaders.length > shown.length && (
        <p className={styles.sectionNote}>
          Showing top {format0(shown.length)} of {format0(leaders.length)}{' '}
          contributors; the totals row reflects all users.
        </p>
      )}
      <div className={styles.tableScroll}>
        <Table size="sm" aria-label="Contribution by user">
          <TableHead>
            <TableRow>
              <TableHeader {...spanBothHeaderRows} scope="col">
                User
              </TableHeader>
              {SOURCE_GROUPS.map((group) => (
                <TableHeader
                  key={group.source}
                  colSpan={group.directions.length}
                  scope="colgroup"
                  className={styles.colSource}
                >
                  {ACTIVITY_SOURCE_LABEL[group.source]}
                </TableHeader>
              ))}
              <TableHeader {...spanBothHeaderRows} scope="col">
                Total
              </TableHeader>
            </TableRow>
            <TableRow>
              {SOURCE_DIRECTIONS.map(({ source, direction }) => (
                <TableHeader
                  key={`${source}-${direction}`}
                  scope="col"
                  className={styles.colDir}
                >
                  {DIRECTION_LABEL[direction]}
                </TableHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((leader) => (
              <TableRow key={leader.actorKey}>
                <TableCell>{leader.actorLabel}</TableCell>
                {SOURCE_DIRECTIONS.map(({ source, direction }) => (
                  <TableCell key={`${source}-${direction}`}>
                    {format0(countFor(leader, source, direction))}
                  </TableCell>
                ))}
                <TableCell>{format0(leader.total)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell>
                <strong>All users</strong>
              </TableCell>
              {SOURCE_DIRECTIONS.map(({ source, direction }) => (
                <TableCell key={`${source}-${direction}`}>
                  <strong>{format0(columnTotal(source, direction))}</strong>
                </TableCell>
              ))}
              <TableCell>
                <strong>{format0(grandTotal)}</strong>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
